'use client';

import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PageSection } from '@/components';
import { useStaggerIn } from '@/hooks/useStaggerIn';
import { MONO_FONT } from '@/theme';

const PHASES = [
  {
    num: 'FASE 01 · MONITOREO',
    title: 'Inteligencia de mercado en los 10 motores.',
    desc: 'Visibilidad relativa, ranking jerárquico, cuota de mercado y rastreo de citas. Lo que se mide se puede mover.',
    items: [
      'Análisis de sentimiento por motor',
      'Gaps de contenido vs. competidores',
      'Mapeo de asociaciones de marca',
      'Precisión y alucinaciones detectadas',
    ],
    tag: '8 métricas · actualización semanal',
    featured: false,
  },
  {
    num: 'FASE 02 · OPTIMIZACIÓN',
    title: 'Construir el Brand Brain que los LLMs sí citan.',
    desc: 'Infraestructura técnica, autoridad editorial y glosarios semánticos. La biblioteca de acciones se activa con priorización por arquetipo.',
    items: [
      'Brand Brain e infraestructura semántica',
      'Autoridad y PR digital Tier-1',
      'Generación de contenido para cerrar brechas (FAQs, artículos, comparativas)',
      'Ciclo de mejora continua mensual',
    ],
    tag: '25 acciones · 6 áreas',
    featured: true,
  },
  {
    num: 'FASE 03 · VENTAS',
    title: 'Impacto en pipeline y retorno medible.',
    desc: 'Tráfico de ultra-intención, ventas aceleradas, eficiencia de adquisición y la frontera siguiente: ventas nativas vía UCP.',
    items: [
      'Tráfico de ultra-intención',
      'Eficiencia comercial',
      'Ventas nativas dentro del chat (UCP)',
      'Atribución por motor y por acción',
    ],
    tag: 'UCP-ready · zero-click commerce',
    featured: false,
  },
];

export default function Phases() {
  const gridRef = useStaggerIn();

  return (
    <PageSection
      eyebrow="02 / Marco Metodológico"
      title={
        <>
          <T>Tres fases. </T>
          <span className="accent">
            <T>Del rastreo</T>
          </span>
          <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />
          <T> a la venta nativa.</T>
        </>
      }
      lede="Tu marca avanza por tres fases del ciclo AEO completo: medir, optimizar y monetizar. No es un dashboard suelto, es una progresión con condiciones de avance explícitas entre una fase y la siguiente."
    >
      <Box
        ref={gridRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          borderTop: '1px solid #e2e5e3',
        }}
      >
        {PHASES.map((phase, i) => (
          <Box
            key={phase.num}
            component="article"
            sx={{
              p: phase.featured ? '40px 32px !important' : undefined,
              py: { xs: 4, md: 5 },
              pr: { md: i < 2 && !phase.featured ? '32px' : undefined },
              pl: { md: i > 0 && !phase.featured ? '32px' : undefined },
              borderRight: {
                md: i < 2 ? '1px solid #e2e5e3' : 'none',
              },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              ...(phase.featured && {
                bgcolor: 'primary.lightest',
                borderTop: '1px solid #e2e5e3',
                borderRadius: '0px',
                margin: '-1px 0',
              }),
            }}
          >
            <Typography
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: phase.featured ? 'primary.darker' : 'text.accent',
                fontWeight: 600,
              }}
            >
              <T>{phase.num}</T>
            </Typography>

            <Typography
              variant="h3"
              component="h3"
              sx={{
                color: phase.featured ? 'primary.darkest' : 'text.primary',
              }}
            >
              <T>{phase.title}</T>
            </Typography>

            <Typography
              sx={{
                fontSize: '15px',
                color: phase.featured ? 'primary.darker' : 'grey.600',
                lineHeight: 1.6,
              }}
            >
              <T>{phase.desc}</T>
            </Typography>

            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                p: 0,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flex: 1,
              }}
            >
              {phase.items.map((item) => (
                <Box
                  component="li"
                  key={item}
                  sx={{
                    fontSize: '14px',
                    color: phase.featured ? 'primary.darker' : 'grey.600',
                    pl: 2,
                    position: 'relative',
                    lineHeight: 1.4,
                    '&::before': {
                      content: '"→"',
                      position: 'absolute',
                      left: 0,
                      color: phase.featured
                        ? 'primary.darkest'
                        : 'primary.main',
                      fontSize: '12px',
                    },
                  }}
                >
                  <T>{item}</T>
                </Box>
              ))}
            </Box>

            <Box
              component="span"
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '10.5px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: phase.featured ? 'primary.darker' : 'grey.600',
                p: '6px 10px',
                bgcolor: phase.featured
                  ? 'rgba(0, 159, 139, 0.15)'
                  : 'grey.100',
                borderRadius: '3px',
                display: 'inline-block',
                alignSelf: 'flex-start',
                mt: 'auto',
              }}
            >
              <T>{phase.tag}</T>
            </Box>
          </Box>
        ))}
      </Box>
    </PageSection>
  );
}
